import Button from 'components/Button';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import Pill from 'components/Pill';
import SectionHeader from 'components/SectionHeader';
import TooltipWrapper from 'components/Tooltip';
import {FC, useState} from 'react';
import {useBackend} from 'state/backend';
import {t} from 'ttag';
import {truncateLabel} from 'utils/functions';

import DeleteTagModal from './DeleteTagModal';
import RenameTagModal from './RenameTagModal';
import {Tag, TagsSettingsBackend} from './types';

const TagsSettings: FC = () => {
    const {
        data: {tags},
    } = useBackend<TagsSettingsBackend>();

    const [tagForRenaming, setTagForRenaming] = useState<Tag | undefined>(
        undefined,
    );
    const [tagForRemoval, setTagForRemoval] = useState<Tag | undefined>(
        undefined,
    );

    return (
        <>
            <NavigationTabs.DocumentSettings selection="tags" />
            <SectionHeader
                subtitle={
                    tags.length > 0
                        ? t`Changes to a tag's name here will also be applied to existing instances of that tag on Documents. Tags can be merged by changing one tag's name to the name of another existing tag.`
                        : t`Add tags to documents to see them here.`
                }
                title={t`Manage Your Tags`}
            />
            <div className="mt-4">
                {tags.map((tag) => (
                    <div
                        className="flex w-full justify-between gap-2 border-b border-b-grey-300 py-3"
                        key={tag.id}
                    >
                        <div className="flex flex-1 flex-wrap">
                            <TooltipWrapper
                                message={
                                    truncateLabel(tag.name, 32) !== tag.name &&
                                    `${tag.name}`
                                }
                            >
                                <Pill
                                    color="grey"
                                    key={tag.id}
                                    label={tag.name}
                                    maxLength={32}
                                />
                            </TooltipWrapper>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                icon="edit"
                                id={`rename-${tag.id}`}
                                kind="secondary"
                                onClick={() => {
                                    setTagForRenaming(tag);
                                }}
                                size="xs"
                            >
                                {t`Rename`}
                            </Button>
                            <Button
                                icon="trash"
                                id={`delete-${tag.id}`}
                                kind="destructive"
                                onClick={() => {
                                    setTagForRemoval(tag);
                                }}
                                size="xs"
                            >
                                {t`Delete`}
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
            {tagForRenaming && (
                <RenameTagModal
                    isOpen={!!tagForRenaming}
                    onClose={() => {
                        setTagForRenaming(undefined);
                    }}
                    tag={tagForRenaming}
                />
            )}
            {tagForRemoval && (
                <DeleteTagModal
                    isOpen={!!tagForRemoval}
                    onClose={() => {
                        setTagForRemoval(undefined);
                    }}
                    tag={tagForRemoval}
                />
            )}
        </>
    );
};

export default TagsSettings;
